import { PayloadAction, ReducerCreators } from '@reduxjs/toolkit';
import { isObject } from 'underscore';

import { ICreateProjectErrors, IPreviewTemplateOverrides, ProjectState } from './types';
import db from '@/utils/indexed-db';
import { BrandliveEvent, EventGroup, EventSettings, EventType } from '@/types/projects.types';
import { GetProject } from '@/connection/projects/get-project';
import { RootState } from '@/store/main';
import { TPartialSessionWithUuid, Session } from '@/types/session.types';
import { GetProjectPublishedStatus } from '@/connection/projects/get-project-published-status';
import { CREATE_FROM_TEMPLATE_IDB_KEY } from '@/utils/create-and-edit-project-helpers/create-from-template.types';
import DeleteSession from '@/connection/sessions/delete-session';
import { GetProjectGroups } from '@/connection/projects/get-project-groups';
import SetupProjectPreview from '@/connection/projects/setup-project-preview';
import { PublishProjectPreview, UpdateProjectPreviewPassword } from '@/connection/projects/publish-project-preview';
import toastTrigger from '@ui/toast/toast-trigger';
import { UpdateEventSettings } from '@/connection/projects/update-event-settings';
import { HomepageHeader } from '@/types/homepage.types';
import { PageModule } from '@/types/module.types';
import { AddLeaderboardNavItem } from '@/connection/leaderboard';
import { RegistrationStep, IUpdateRegStepsOptions, RegFieldsEnum } from '@/types/registration.types';
import {
  sortWorkingEventRegistrationQuestions,
  sortRegistrationSteps,
  getRegistrationQuestionsFromRegistrationSteps,
  GetDefaultRegistrationSteps,
} from '../../../utils/registration/registration';
import updateHomepageModuleReducerHelper from './helpers/update-homepage-module-reducer-helper';
import loadWorkingProjectWithIndexedDBReducerHelper from './helpers/load-working-project-with-indexed-db-reducer-helper';
import { GetProjectScheduledEmails } from '@/connection/emails/get-project-scheduled-emails';
import publishWorkingProjectReducerHelper from './helpers/publish-working-project-reducer-helper';
import { EditScheduledEmail } from '@/connection/emails/edit-scheduled-email';
import { IScheduledEmail } from '@/types/email.types';
import DuplicateSession from '@/connection/sessions/duplicate-session';
import { FetchError } from '@/connection/helpers';
import { getBaseAdminURL } from '@/utils/urls';
import { selectUserChannel } from '../user/selectors';
import { Channel } from '@/types/channel.types';
import { ThemePack } from '@/types/theme-packs.types';
import { GetMostRecentActivityLog } from '@/connection/activity-log/get-activity-log';
import { IActivityTypes } from '@/types/activity-log.types';
import { UpdateRegistrationSteps } from '@/connection/registration/registration-steps';
import { updateRegistrationSteps } from '.';
import DuplicateProject from '@/connection/projects/duplicate-project';
import { GetSession } from '@/connection/projects/get-session';
import { UpdatePublishSettings } from '@/connection/settings/update-publish-settings';
import { UpdateFavicon } from '@/connection/settings/update-favicon';
import { GetGreenroomMeetingData } from '@/connection/greenroom/get-greenroom-meeting-data';
import LoadGreenroomShots from '@/connection/greenroom/load-greenroom-shots';
import GetMedia from '@/connection/greenroom/get-media';
import LoadGraphics from '@/connection/greenroom/load-graphics';

export const reducers = (create: ReducerCreators<ProjectState>) => ({
  loadProject: create.asyncThunk(
    async (
      {
        uuid,
        returnStoreStateIfExists,
        /* do not remove doNotSetLoadingState from types, even though it's not used here, it's used during pending state */
      }: { uuid: string; returnStoreStateIfExists?: boolean; doNotSetLoadingState?: boolean },
      { rejectWithValue, getState }
    ) => {
      try {
        if (returnStoreStateIfExists) {
          const state = getState() as RootState;
          const proj = state.project?.workingProject as BrandliveEvent | null;
          if (proj && proj.uuid === uuid) {
            return proj;
          }
        }
        const result = await GetProject(uuid);
        if (!result) {
          throw rejectWithValue('Error loading project');
        }
        if ('error' in result) {
          console.error('loadProject error', result.error);
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.workingProject = action.payload;
        }
        state.loadingWorkingProject = false;
      },
      pending: (state, action) => {
        if (!action.meta?.arg.doNotSetLoadingState) {
          state.loadingWorkingProject = true;
        }
      },
      rejected: (state, action) => {
        console.error(action.payload);
        if (action.payload instanceof FetchError) {
          if (action.payload.response?.status === 403) {
            toastTrigger(
              ` <div class="S1">
                  <div>You do not have permission to access this project. Redirecting to admin console</div>
                </div>`,
              {
                type: 'error',
                autoClose: 5000,
              }
            );
            setTimeout(() => {
              window.location.href = getBaseAdminURL() + '/admin';
            }, 4500);
          } else {
            toastTrigger(
              ` <div class="S1">
                  <div>Error loading project, please try again later</div>
                </div>`,
              {
                type: 'error',
                autoClose: 5000,
              }
            );
            state.loadingWorkingProjectError = 'Error loading project';
          }
        } else if (action.payload instanceof Error) {
          state.loadingWorkingProjectError = action.payload?.message || 'Error loading project';
        } else {
          state.loadingWorkingProjectError = action.error?.message || 'Error loading project';
        }
        state.loadingWorkingProject = false;
      },
    }
  ),
  loadPreviewTemplate: create.asyncThunk(
    async (
      { uuid, returnStoreStateIfExists }: { uuid: string; returnStoreStateIfExists?: boolean },
      { rejectWithValue, getState }
    ) => {
      try {
        if (returnStoreStateIfExists) {
          const state = getState() as RootState;
          const proj = state.project?.workingProject as BrandliveEvent | null;
          if (proj && proj.uuid === uuid) {
            return proj;
          }
        }
        const result = await GetProject(uuid);
        if (!result) {
          throw rejectWithValue('Error loading project');
        }
        if ('error' in result) {
          console.error('loadProject error', result.error);
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.previewTemplate = action.payload;
        }
        state.loadingPreviewTemplate = false;
      },
      pending: (state) => {
        state.loadingPreviewTemplate = true;
      },
      rejected: (state, action) => {
        if (action.payload instanceof Error) {
          console.error(action.payload);
          state.loadingPreviewTemplateError = action.payload?.message || 'Error loading project';
        } else {
          console.error(action.error);
          state.loadingPreviewTemplateError = action.error?.message || 'Error loading project';
        }
        state.loadingPreviewTemplate = false;
      },
    }
  ),
  getPublishedEventUrl: create.asyncThunk(
    async ({ eventUuid }: { eventUuid: string }, { rejectWithValue }) => {
      try {
        const result = await GetProjectPublishedStatus(eventUuid);
        if ('error' in result) {
          console.error('getPublishedEventUrl error', result.error);
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.projectPublishedStatus = action.payload;
          state.checkingPublishedStatus = false;
        }
      },
      pending: (state) => {
        state.checkingPublishedStatus = true;
      },
      rejected: (state, action) => {
        state.checkingPublishedStatus = false;
        console.error(action.error);
      },
    }
  ),
  clearPublishedEventUrl: create.reducer((state) => {
    state.projectPublishedStatus = null;
    state.checkingPublishedStatus = false;
  }),
  setCreateProjectErrorFields: create.reducer((state, action: PayloadAction<ICreateProjectErrors | null>) => {
    state.createProjectErrors = action.payload;
  }),
  removeCreateProjectErrorFields: create.reducer((state, action: PayloadAction<Partial<ICreateProjectErrors>>) => {
    if (state.createProjectErrors) {
      state.createProjectErrors = {
        ...state.createProjectErrors,
        ...action.payload /* spreading because it's <Partial> */,
      };
    }
  }),
  setWorkingProject: create.reducer((state, action: PayloadAction<BrandliveEvent | null>) => {
    state.workingProject = action.payload;
  }),
  /**
   * saveWorkingProject triggers save-project-middleware.ts
   *
   */
  saveWorkingProject: create.reducer(
    (state, action: PayloadAction<{ project: BrandliveEvent; sessions?: Session[]; saveId?: string | null }>) => {
      // This update triggers in a debounce inside of save-project-middleware.ts
      state.savingWorkingProject = true;
      state.saveId = action.payload.saveId ?? state.saveId;
      state.workingProject = action.payload.project;
      // by adding sessionsToUpdateAfterProjectSave, save-project-middleware will update the session after the project is saved
      state.sessionsToUpdateAfterProjectSave = action.payload.sessions || null;

      // Update activity tracking
      state.mostRecentActivityLogUpdate = new Date().toISOString();
      state.mostRecentActivityLogType = IActivityTypes.events;
    }
  ),
  /**
   * Why by fields?
   *
   * So we don't have to import the whole project into each component which causes unnecessary re-renders.
   *
   * saveWorkingProjectByFields triggers save-project-by-fields-middleware.ts
   *
   * ************
   * ************
   * IMPORTANT: This only works on top level fields of the project object.
   * You can still use this to update nested fields, but you need to pass the entire object into the payload.
   * ************
   * ************
   */
  saveWorkingProjectByFields: create.reducer(
    (
      state,
      action: PayloadAction<
        Partial<BrandliveEvent> & {
          saveId?: string;
          sessionsToSave?: TPartialSessionWithUuid[];
          refetchProjectAfterSave?: boolean;
        }
      >
    ) => {
      if (!state.workingProject) return;

      state.savingWorkingProject = true;

      const { saveId, sessionsToSave, refetchProjectAfterSave, ...updatedProjectFields } = action.payload;

      state.saveId = saveId ?? state.saveId;
      state.refetchProjectAfterSave = !!refetchProjectAfterSave;

      // update project keys
      Object.entries(updatedProjectFields).forEach((data) => {
        const key = data[0];
        const value = data[1];
        if (state.workingProject && key in state.workingProject) {
          // @ts-expect-error using dynamic key makes typescript think this is "any" and type casting doesn't fix it
          state.workingProject[key] = value;
        }
      });

      // update sessions AFTER project so we get both update fields just in case someone passes in updates to a session
      // in both places - not sure why they'd do that, but just in case.
      // by adding sessionsToUpdateAfterProjectSave, save-project-middleware will update the session after the project is saved
      if (state.workingProject?.sessions && sessionsToSave) {
        const sessionsToSaveMap = new Map(sessionsToSave.map((s) => [s.uuid, s.uuid]));
        const allSessions = state.workingProject.sessions.map((s) => {
          const foundSession = sessionsToSave.find((sts) => sts.uuid === s.uuid);
          if (!foundSession) return s;
          return {
            ...s,
            ...foundSession,
          };
        });
        // update sessions in redux first
        state.workingProject.sessions = allSessions;
        // then extract only the sessions that actually require updating and pass them into the save mechanism
        const onlyUpdatedSessions = allSessions.filter((s) => sessionsToSaveMap.has(s.uuid));
        state.sessionsToUpdateAfterProjectSave = onlyUpdatedSessions || null;

        // update working session if its one of the updated sessions
        if (state.workingSession) {
          const updatedWorkingSession = allSessions.find((s) => s.uuid === state.workingSession?.uuid);
          if (updatedWorkingSession) {
            state.workingSession = updatedWorkingSession;
          }
        }

        // Update activity tracking
        state.mostRecentActivityLogUpdate = new Date().toISOString();
        state.mostRecentActivityLogType = IActivityTypes.events;
      }
    }
  ),
  clearSaveSessionAfterProjectSave: create.reducer((state) => {
    state.sessionsToUpdateAfterProjectSave = null;
  }),
  setSavingWorkingProject: create.reducer((state, action: PayloadAction<boolean>) => {
    state.savingWorkingProject = action.payload;
    if (!action.payload) {
      state.saveId = null;
      state.refetchProjectAfterSave = false;
    }
  }),
  setSavingWorkingProjectError: create.reducer((state, action: PayloadAction<string | null>) => {
    state.savingWorkingProjectError = action.payload;
    state.saveId = null;
    state.refetchProjectAfterSave = false;
  }),
  saveWorkingProjectSession: create.reducer((state, action: PayloadAction<Session>) => {
    // These updates trigger in a debounce inside of save-session-middleware.ts
    state.savingWorkingProject = true;
    if (state.workingProject?.sessions) {
      // updatedSessionUuid is required to keep track of the session that is being updated in save-session-middleware.ts
      const sessionData = action.payload;
      state.updatedSessionUuid = action.payload.uuid;
      state.workingProject.sessions = state.workingProject.sessions.map((s) => {
        if (s.uuid === action.payload.uuid) {
          return action.payload;
        }
        return s;
      });

      if (sessionData.uuid === state.workingSession?.uuid) {
        state.workingSession = {
          ...state.workingSession,
          ...sessionData,
        };
      }
    }
  }),
  saveWorkingProjectSessionByField: create.reducer(
    (
      state,
      action: PayloadAction<Partial<Session> & { uuid: string; saveId?: string; refetchProjectAfterSave?: boolean }>
    ) => {
      const { saveId, refetchProjectAfterSave, ...sessionData } = action.payload;
      // These updates trigger in a debounce inside of save-session-middleware.ts
      state.savingWorkingProject = true;
      state.refetchProjectAfterSave = !!refetchProjectAfterSave;
      state.saveId = saveId ?? state.saveId;
      if (state.workingProject?.sessions) {
        state.updatedSessionUuid = sessionData.uuid;
        state.workingProject.sessions = state.workingProject.sessions.map((s) => {
          if (s.uuid === sessionData.uuid) {
            return {
              ...s,
              ...sessionData,
            };
          }
          return s;
        });
        // Also update working session if it's the same session
        if (sessionData.uuid === state.workingSession?.uuid) {
          state.workingSession = {
            ...state.workingSession,
            ...sessionData,
          };
        }
      }
    }
  ),
  setUpdatingSessionUuid: create.reducer((state, action: PayloadAction<string | null>) => {
    // This is required to keep track of the session that is being updated
    // inside of save-session-middleware.ts
    state.updatedSessionUuid = action.payload;
  }),
  loadWorkingProjectWithIndexedDB: create.asyncThunk(
    async (
      {
        project,
        projectUuid,
        resetValues,
        withCache = true,
      }: { project: BrandliveEvent | null; projectUuid?: string; resetValues?: boolean; withCache?: boolean },
      { rejectWithValue, getState }
    ) => {
      try {
        let _project = project;

        const state = getState() as RootState;

        if (state.project.loadingWorkingProject) return null;

        const result = await loadWorkingProjectWithIndexedDBReducerHelper({
          project,
          projectUuid,
          resetValues,
          withCache,
          rejectWithValue,
          getState,
        });
        if (result) {
          _project = result;
        }

        return _project;
      } catch (error) {
        console.error(error);
        throw rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.workingProject = action.payload;
        }
        state.loadingWorkingProject = false;
      },
      pending: (state) => {
        state.loadingWorkingProject = true;
      },
      rejected: (state) => {
        state.loadingWorkingProject = false;
      },
    }
  ),
  updateWorkingProjectWithIndexedDb: create.asyncThunk(
    async (project: BrandliveEvent | null, { rejectWithValue }) => {
      try {
        if (!project) {
          await db.delete(CREATE_FROM_TEMPLATE_IDB_KEY, CREATE_FROM_TEMPLATE_IDB_KEY);
          return null;
        }

        await db.put({
          uuid: CREATE_FROM_TEMPLATE_IDB_KEY,
          type: CREATE_FROM_TEMPLATE_IDB_KEY,
          data: project,
        });
        return project;
      } catch (error) {
        console.error(error);
        throw rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.workingProject = action.payload || null;
        state.updatingWorkingProject = false;
      },
      pending: (state) => {
        state.updatingWorkingProject = true;
      },
      rejected: (state) => {
        state.updatingWorkingProject = false;
      },
    }
  ),
  deleteSession: create.asyncThunk(
    async ({ projectUuid, sessionId }: { projectUuid: string; sessionId: number }, { rejectWithValue, getState }) => {
      try {
        const result = await DeleteSession({
          eventUuid: projectUuid,
          sessionID: sessionId,
        });

        if ('error' in result) {
          console.error('deleteSession error', result.error);
          throw rejectWithValue(result.error);
        }

        // if we are deleting the second last session and reverting to single session,
        // we need to set the event type to match the channel project type, so we pass it down
        const state = getState() as RootState;
        /* Even though selectUserChannel is implicitly typed, we need to cast it here or this whole file starts screaming */
        const userChannel = selectUserChannel(state) as Channel | null;
        const channelProjectType = userChannel?.type;

        return {
          session: result.session,
          channelProjectType: channelProjectType || EventType.Event,
        };
      } catch (e) {
        console.error(e);
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.workingProject) {
          state.workingProject.sessions = state.workingProject.sessions?.filter(
            (s) => s.session !== action.payload.session
          );
          // if there is only one session left, we set the event type to be the channel type
          // this happens in the api, so here we are just syncing the state
          if (state.workingProject.sessions.length === 1 && action.payload.channelProjectType) {
            state.workingProject.event_type = action.payload.channelProjectType;
          }
        }
        state.deletingSession = false;
      },
      pending: (state) => {
        state.deletingSession = true;
      },
      rejected: (state) => {
        state.deletingSession = false;
      },
    }
  ),
  clearPreviewTemplate: create.reducer((state) => {
    state.previewTemplate = null;
  }),
  loadProjectGroups: create.asyncThunk(
    async (channel: number, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState;
        const projectGroups = state.project?.projectGroups as EventGroup[] | null;
        if (projectGroups) return projectGroups;

        const result = await GetProjectGroups({ channel, templatesOnly: true });
        if ('error' in result) {
          console.error('loadProjectGroups error', result.error);
          throw rejectWithValue(result.error);
        }
        return result || [];
      } catch (e) {
        console.error(e);
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.projectGroups = action.payload;
      },
      pending: (state) => {
        state.projectGroups = null;
      },
      rejected: (state, action) => {
        state.projectGroups = null;
        if (action.payload instanceof Error) {
          console.error(action.payload);
          state.loadingWorkingProjectError = action.payload?.message || 'Error loading project groups';
        } else {
          console.error(action.error);
          state.loadingWorkingProjectError = action.error?.message || 'Error loading project groups';
        }
        state.loadingWorkingProject = false;
      },
    }
  ),
  setupProjectPreview: create.asyncThunk(
    async ({ eventUuid, channel }: { eventUuid: string; channel: number }, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState;
        const workingProject = (state.project as ProjectState)?.workingProject as BrandliveEvent | null;
        if (workingProject?.event_preview) {
          return workingProject.event_preview;
        }
        const result = await SetupProjectPreview(eventUuid, channel);
        if (!result) {
          throw rejectWithValue('Error loading project');
        }
        if ('error' in result) {
          console.error('loadProject error', result.error);
          throw rejectWithValue(result.error);
        }

        await PublishProjectPreview(result.uuid, eventUuid, result.url, channel);

        return result;
      } catch (e) {
        console.error(e);
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          if (state.workingProject) {
            state.workingProject.event_preview = action.payload;
          }
        }
        state.settingUpProjectPreview = false;
      },
      pending: (state) => {
        state.settingUpProjectPreview = true;
      },
      rejected: (state, action) => {
        state.settingUpProjectPreview = false;
        const payload = isObject(action?.payload) ? action?.payload : null;
        const fallbackMsg =
          payload?.message ||
          payload?.error ||
          `${state.workingProject?.name || 'Project'} preview could not be set up. Please refresh.`;
        const message = typeof payload === 'string' ? payload : fallbackMsg;
        toastTrigger(message, {
          type: 'error',
        });
      },
    }
  ),
  updateProjectSettings: create.asyncThunk(
    async ({ event_uuid, settings }: { event_uuid: string; settings: EventSettings }) => {
      try {
        const result = await UpdateEventSettings(event_uuid, settings);

        return result;
      } catch (e) {
        console.error(e);
      }
    },
    {
      pending: (state) => {
        if (state.workingProject) {
          state.isSettingsUpdating = true;
        }
      },
      fulfilled: (state, action) => {
        if (state.workingProject) {
          state.workingProject.settings = action.payload as EventSettings;
          state.isSettingsUpdating = false;
        }
      },
    }
  ),
  setRegistrationOn: create.asyncThunk(
    async (isOn: boolean, { rejectWithValue, getState, dispatch }) => {
      try {
        const state = getState() as RootState;
        if (state.project.workingProject && !state.project.workingProject.registration_steps?.length) {
          const defaultSteps = GetDefaultRegistrationSteps();

          dispatch(updateRegistrationSteps({ steps: defaultSteps }));
          await UpdateRegistrationSteps(state.project.workingProject.uuid, defaultSteps);
        }

        return isOn;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.workingProject) {
          state.workingProject.registration_on = action.payload;
        }
      },
    }
  ),

  updateHomepageHeader: create.reducer((state, action: PayloadAction<HomepageHeader>) => {
    if (state.workingProject && state.workingProject?.homepage) {
      state.workingProject = {
        ...state.workingProject,
        homepage: {
          ...state.workingProject.homepage,
          header: action.payload,
        },
      };
    }
  }),
  updateHomepageModule: create.asyncThunk(
    (data: { module: PageModule }, { dispatch, getState }) => {
      // We are not using the fulfilled state in this case because
      // the fulfilled state will trigger before the saveWorkingProject action is completed
      // because the saveWorkingProject action is a debounced async action,
      // however, we immediately call savingWorkingProject in the pending state
      // so you can use `savingWorkingProject` state if you need to "wait" for this action to complete in your component
      const state = getState() as RootState;

      updateHomepageModuleReducerHelper({ data, dispatch, state });

      return data.module;
    },
    {
      pending: (state) => {
        if (state.workingProject?.homepage) {
          // not this will get reset back to false automatically in the save-project middleware
          // when we call dispatch(saveWorkingProject) at the end if this if statement
          state.savingWorkingProject = true;
        }
      },
      rejected: (_state, action) => {
        console.error(action.error);
      },
    }
  ),
  addLeaderboardNavItem: create.asyncThunk(
    async (data: { eventUuid: string; homepageId: number }, { rejectWithValue }) => {
      try {
        return await AddLeaderboardNavItem(data.eventUuid, data.homepageId);
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload && state?.workingProject?.homepage?.event_main_nav_items) {
          state.workingProject.homepage.event_main_nav_items = [
            ...state.workingProject.homepage.event_main_nav_items,
            action.payload,
          ];
        }
      },
    }
  ),
  updateRegistrationSteps: create.reducer(
    (state, action: PayloadAction<{ steps: RegistrationStep[]; options?: IUpdateRegStepsOptions }>) => {
      if (state.workingProject) {
        const updatedSteps = action.payload.steps;
        const options = action.payload?.options;

        const registrationSteps = sortRegistrationSteps(updatedSteps);
        const registrationQuestions = getRegistrationQuestionsFromRegistrationSteps(registrationSteps);

        const requiredRegistrationQuestions = () => {
          // set required questions to email only if makeNameOptional is true
          // Note: if the event has leaderboard or breakout sessions then first/last are required for those features and this will be overwritten
          // it will be overwritten in `registration-required-fields.ts` -> `useSetRequiredField` -> `dispatch(updateRequiredQuestions(updatedReqQuestions));`
          if (options?.makeNameOptional) {
            return [RegFieldsEnum.email];
          }
          return options?.requiredQuestions || state.workingProject?.required_registration_questions || null;
        };

        // sort state.workingEvent.registration_questions
        const updatedRegistrationQnOrder = sortWorkingEventRegistrationQuestions({
          registrationSteps,
          registrationQuestions: registrationQuestions,
          registrationQuestionsToAppend: options?.registrationQuestionsToAppend,
        });

        // remove duplicates
        // the order does matter, however the previous sort should have already sorted the questions
        // so any duplicates should be next to each other
        const updatedRegistrationQnOrderNoDups = Array.from(
          new Map(updatedRegistrationQnOrder.map((q) => [q.registration_question, q])).values()
        );

        state.workingProject.registration_steps = registrationSteps;
        state.workingProject.required_registration_questions = requiredRegistrationQuestions();
        state.workingProject.registration_questions = updatedRegistrationQnOrderNoDups;
      }
    }
  ),
  setActivePreviewPageTab: create.reducer((state, action: PayloadAction<string | EventType | null>) => {
    state.activePreviewPageTab = action.payload;
  }),
  setDisplayEditProjectThemeTray: create.reducer((state, action: PayloadAction<boolean | null>) => {
    if (action.payload === null) {
      state.displayEditProjectThemeTray = !state.displayEditProjectThemeTray;
    } else {
      state.displayEditProjectThemeTray = action.payload;
    }
  }),
  setDisplayPreviewTemplateThemeTray: create.reducer((state, action: PayloadAction<boolean | null>) => {
    if (action.payload === null) {
      state.displayPreviewTemplateThemeTray = !state.displayPreviewTemplateThemeTray;
    } else {
      state.displayPreviewTemplateThemeTray = action.payload;
    }
  }),
  setShowUpdateProjectTypeModal: create.reducer((state, action: PayloadAction<EventType | null>) => {
    state.showUpdateProjectTypeModal = action.payload;
  }),
  publishWorkingProject: create.asyncThunk(
    async ({ publishedUrl }: { publishedUrl?: string }, { rejectWithValue, getState, dispatch }) => {
      try {
        const rootState = getState() as RootState;

        if (rootState.project.savingWorkingProject) {
          throw rejectWithValue(
            'Publish attempted during project save. Please wait a few seconds until the save is complete and try again.'
          );
        }

        const workingProjectUuid = (getState() as RootState).project?.workingProject?.uuid;
        if (!workingProjectUuid) {
          throw rejectWithValue('No working project found to publish');
        }
        await publishWorkingProjectReducerHelper({ dispatch, getState, publishedUrl });

        const refetchedProject = await loadWorkingProjectWithIndexedDBReducerHelper({
          project: null,
          projectUuid: workingProjectUuid,
          resetValues: false,
          withCache: false,
          rejectWithValue,
          getState,
        });

        let publishedStatus = rootState.project.projectPublishedStatus as { status: number; url: string } | null;
        if (publishedUrl) {
          // refetch the published status to update the URL
          const refetchedPublishedStatus = await GetProjectPublishedStatus(workingProjectUuid);
          if ('error' in refetchedPublishedStatus) {
            console.error('getPublishedEventUrl error', refetchedPublishedStatus.error);
          } else {
            publishedStatus = refetchedPublishedStatus;
          }
        }

        return { project: refetchedProject, publishedStatus };
      } catch (e) {
        console.error(e);
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.publishingProject = false;
        if (action.payload?.project) {
          state.workingProject = action.payload.project;
        }
        if (action.payload?.publishedStatus) {
          state.projectPublishedStatus = action.payload.publishedStatus;
        }
      },
      pending: (state) => {
        state.publishingProject = true;
      },
      rejected: (state, action) => {
        state.publishingProject = false;
        console.error(action);
        let msg = isObject(action.payload) ? action.payload?.payload : '';
        if (!msg && action.payload instanceof Error) {
          msg = action.payload.message || 'An error occurred while publishing the project.';
        }
        if (!msg) {
          msg = action.error?.message || 'An error occurred while publishing the project.';
        }
        toastTrigger(
          `<div>
            <p class="S1">${msg}</p>
          </div>`,
          {
            type: 'error',
            autoClose: 10000,
            closeOnClick: true,
          }
        );
      },
    }
  ),
  editScheduledEmail: create.asyncThunk(
    async ({ email }: { email: IScheduledEmail }, { rejectWithValue }) => {
      try {
        const result = await EditScheduledEmail(email);
        if ('error' in result) {
          console.error('editScheduledEmail error', result.error);
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (error) {
        console.error('Error editing scheduled email:', error);
        throw rejectWithValue('Error editing scheduled email');
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload && state.scheduledEmails) {
          const updatedEmails = state.scheduledEmails.map((email) => {
            if (email.uuid === action.payload.uuid) {
              return action.payload;
            }
            return email;
          });
          state.scheduledEmails = updatedEmails;
        }
        state.loadingScheduledEmails = false;
      },
      pending: (state) => {
        state.loadingScheduledEmails = true;
      },
      rejected: (state, action) => {
        console.error('Error editing scheduled email:', action.error);
        state.loadingScheduledEmails = false;
        toastTrigger(
          `<div>
            <h4 class="H4">There was an error editing the scheduled email.</h4>
            <p class="S1">${action.payload instanceof Error ? action.payload.message : action.error.message}</p>
          </div>`,
          {
            type: 'error',
            autoClose: 5000,
          }
        );
      },
    }
  ),
  getProjectScheduledEmails: create.asyncThunk(
    async ({ projectUuid }: { projectUuid: string }, { rejectWithValue }) => {
      try {
        const result = await GetProjectScheduledEmails({ projectUuid });
        if ('error' in result) {
          console.error('getProjectScheduledEmails error', result.error);
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (error) {
        console.error('Error fetching scheduled emails:', error);
        throw rejectWithValue('Error fetching scheduled emails');
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.scheduledEmails = action.payload;
        }
        state.loadingScheduledEmails = false;
      },
      pending: (state) => {
        state.loadingScheduledEmails = true;
      },
      rejected: (state, action) => {
        console.error('Error loading scheduled emails:', action.error);
        state.loadingScheduledEmails = false;
      },
    }
  ),
  setSelectEditOrPublishButtonState: create.reducer((state, action: PayloadAction<'editor' | 'publish'>) => {
    state.selectEditOrPublishButtonState = action.payload;
  }),
  setShowUneditedContent: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showUneditedContent = action.payload;
  }),
  setPreviewTemplateOverrides: create.reducer((state, action: PayloadAction<IPreviewTemplateOverrides>) => {
    // intentionally using undefined so that null values can be set
    if (action.payload?.themeMode !== undefined) {
      state.previewTemplateOverrides.themeMode = action.payload.themeMode;
    }
    if (action.payload?.themePack !== undefined) {
      state.previewTemplateOverrides.themePack = action.payload.themePack;
    }
  }),
  setIsCreatingNewSession: create.reducer((state, action: PayloadAction<boolean>) => {
    state.isCreatingNewSession = action.payload;
  }),
  duplicateSession: create.asyncThunk(
    async (
      { event, projectUuid, session }: { event: number; projectUuid: string; session: Session },
      { rejectWithValue }
    ) => {
      try {
        const result = await DuplicateSession({
          event,
          projectUuid,
          session,
        });
        if ('error' in result) {
          console.error('duplicateSession error', result.error);
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (e) {
        console.error(e);
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        const newSession = action.payload;
        state.duplicatedSession = newSession;
        if (newSession && state.workingProject) {
          newSession.timestamp = new Date(newSession.timestamp || 0).valueOf();
          newSession.end_timestamp = new Date(newSession.end_timestamp || 0).valueOf();
          state.workingProject.sessions = [newSession, ...(state.workingProject?.sessions ?? [])];
          state.workingProject.settings = {
            ...state.workingProject.settings,
            display_homepage: state.workingProject.sessions.length > 1,
          };
        }
      },
      pending: (state) => {
        state.duplicatedSession = null;
        state.duplicatingSession = true;
      },
      rejected: (state, action) => {
        state.duplicatedSession = null;
        console.error('Error duplicating session:', action.error);
        toastTrigger(
          `<div>
            <h4 class="H4">There was an error duplicating the session.</h4>
            <p class="S1">${action.payload instanceof Error ? action.payload.message : action.error.message}</p>
          </div>`,
          {
            type: 'error',
            autoClose: 5000,
          }
        );
      },
      settled: (state) => {
        state.duplicatingSession = false;
      },
    }
  ),
  setLoadingGreenroom: create.reducer((state, action: PayloadAction<boolean>) => {
    state.loadingGreenroom = action.payload;
  }),
  setWorkingProjectTheme: create.reducer((state, action: PayloadAction<ThemePack>) => {
    if (state.workingProject) {
      state.workingProject.settings.design.colors = {
        ...action.payload,
        updated_at: new Date().toISOString(),
      };
    }
  }),
  updateProjectPreviewPassword: create.asyncThunk(
    async (project_uuid: string, { rejectWithValue }) => {
      try {
        const result = await UpdateProjectPreviewPassword(project_uuid);
        return result;
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action?.payload?.password && state.workingProject?.event_preview) {
          state.workingProject.event_preview.password = action.payload.password;
          state.updatingProjectPreviewPassword = false;
        }
      },
      pending: (state) => {
        state.updatingProjectPreviewPassword = true;
      },
      rejected: (state, action) => {
        state.updatingProjectPreviewPassword = false;
        console.error('Error updating project preview password', action.error);
        toastTrigger(
          `<div>
            <h4 class="H4">There was a problem updating the project preview password.</h4>
          </div>`,
          {
            type: 'error',
            autoClose: 5000,
          }
        );
      },
    }
  ),
  loadMostRecentActivityLog: create.asyncThunk(
    async (data: { channel: number; projectUuid: string }, { rejectWithValue }) => {
      try {
        return await GetMostRecentActivityLog(data.channel, data.projectUuid);
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        const mostRecentActivity = action.payload.activities?.[0];

        if (!mostRecentActivity) return;

        state.mostRecentActivityLogUpdate = mostRecentActivity.timestamp;
        state.mostRecentActivityLogType = mostRecentActivity.type;
      },
    }
  ),
  updateMostRecentActivity: create.reducer((state, action?: PayloadAction<{ isPublish: boolean }>) => {
    state.mostRecentActivityLogUpdate = new Date().toISOString();
    // It currently doesn't matter what type this. It's either a publish action, or anything else.
    state.mostRecentActivityLogType = action?.payload.isPublish
      ? IActivityTypes.published_events
      : IActivityTypes.events;
  }),
  loadGreenroomMeeting: create.asyncThunk(
    async ({ sessionUuid, token }: { sessionUuid: string; token: string }, { rejectWithValue }) => {
      if (!token) {
        throw rejectWithValue(new Error('No ID Token'));
      }

      return GetGreenroomMeetingData(sessionUuid, token);
    },
    {
      fulfilled: (state, action) => {
        state.greenroomMeeting = action.payload;
        state.greenroomMeetingError = false;
      },
      pending: (state) => {
        state.greenroomMeetingLoading = true;
      },
      settled: (state) => {
        state.greenroomMeetingLoading = false;
      },
      rejected: (state, action) => {
        console.error(action);
        state.greenroomMeetingError = true;
      },
    }
  ),
  loadGreenroomData: create.asyncThunk(
    async ({ meetingId, token }: { meetingId: number; token: string }, { rejectWithValue }) => {
      if (!token) {
        throw rejectWithValue(new Error('No ID Token'));
      }

      if (!meetingId) {
        throw rejectWithValue(new Error('No Meeting ID'));
      }

      return Promise.allSettled([
        LoadGreenroomShots(meetingId, token),
        GetMedia(meetingId, token),
        LoadGraphics(meetingId, token),
      ]);
    },
    {
      fulfilled: (state, action) => {
        const [shotsRes, mediaRes, graphicsRes] = action.payload;
        if (shotsRes.status !== 'fulfilled' || !Array.isArray(shotsRes.value)) {
          state.greenroomShotsError = true;
        } else {
          state.greenroomShots = shotsRes.value.toSorted((a, b) => a.shot_data.order - b.shot_data.order);
          state.greenroomShotsError = false;
        }

        if (mediaRes.status !== 'fulfilled' || !Array.isArray(mediaRes.value)) {
          state.greenroomMediaError = true;
        } else {
          state.greenroomMedia = mediaRes.value;
          state.greenroomMediaError = false;
        }

        if (graphicsRes.status !== 'fulfilled' || !Array.isArray(graphicsRes.value)) {
          state.greenroomGraphicsError = true;
        } else {
          state.greenroomGraphics = graphicsRes.value;
          state.greenroomGraphicsError = false;
        }
      },
      pending: (state) => {
        state.greenroomMediaLoading = true;
        state.greenroomShotsLoading = true;
        state.greenroomGraphicsLoading = true;
      },
      settled: (state) => {
        state.greenroomMediaLoading = false;
        state.greenroomShotsLoading = false;
        state.greenroomGraphicsLoading = false;
      },
      rejected: (state, action) => {
        console.error(action);
        state.greenroomMediaError = true;
        state.greenroomShotsError = true;
        state.greenroomGraphicsError = true;
      },
    }
  ),
  duplicateProject: create.asyncThunk(
    async ({ projectUuid, channel }: { projectUuid: string; channel: number }, { rejectWithValue }) => {
      try {
        const result = await DuplicateProject({
          projectUuid,
          channel,
        });
        if ('error' in result) {
          throw rejectWithValue(result.error);
        }
        return result;
      } catch (e) {
        throw rejectWithValue(e);
      }
    },
    {
      fulfilled: (state, action) => {
        state.isProjectDuplicating = false;
        state.duplicatedProject = action.payload;
      },
      pending: (state) => {
        state.isProjectDuplicating = true;
        state.duplicatedProject = null;
      },
      rejected: (state, action) => {
        state.duplicatedProject = null;
        console.error('Error duplicating project:', action.error);
        toastTrigger(
          `<div>
            <h4 class="H4">There was an error duplicating the project.</h4>
            <p class="S1">${action.payload instanceof Error ? action.payload.message : action.error.message}</p>
          </div>`,
          {
            type: 'error',
            autoClose: 5000,
          }
        );
      },
      settled: (state) => {
        state.isProjectDuplicating = false;
      },
    }
  ),
  loadWorkingSession: create.asyncThunk(
    async (
      { sessionId, returnStoreStateIfExists }: { sessionId: number; returnStoreStateIfExists: boolean },
      { rejectWithValue, getState }
    ) => {
      if (returnStoreStateIfExists) {
        const state = getState() as RootState;
        const session = state.project?.workingSession as Session | null;
        if (session && session.session === sessionId) {
          return session;
        }
      }
      try {
        return await GetSession(sessionId);
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.workingSession = action.payload;
        state.loadingWorkingSession = false;
      },
      pending: (state) => {
        state.loadingWorkingSession = true;
      },
      rejected: (state, action) => {
        state.loadingWorkingSession = false;
        state.loadingWorkingSessionError = action.error.message;

        toastTrigger(
          ` <div class="S1">
				<div>Unable to load session</div>
			</div>`,
          {
            type: 'error',
            autoClose: 5000,
          }
        );
      },
    }
  ),
  updatePublishSettings: create.asyncThunk(
    async ({ project_uuid, isLocked, dmaId }: { project_uuid: string; isLocked?: boolean; dmaId?: string }) => {
      try {
        const result = await UpdatePublishSettings(project_uuid, isLocked, dmaId);

        return result;
      } catch (e) {
        console.error(e);
      }
    },
    {
      pending: (state) => {
        if (state.workingProject) {
          state.isSettingsUpdating = true;
        }
      },
      fulfilled: (state, action) => {
        if (state.workingProject && action.payload) {
          state.workingProject.dma_id = action.payload?.dma_id;
          state.workingProject.is_publish_locked = action.payload?.is_publish_locked;
          state.isSettingsUpdating = false;
        }
      },
      rejected: (state) => {
        state.isSettingsUpdating = false;
      },
    }
  ),
  setFavicon: create.asyncThunk(
    async ({ project_uuid, favicon }: { project_uuid: string; favicon?: string }) => {
      try {
        const result = await UpdateFavicon(project_uuid, favicon);

        return result;
      } catch (e) {
        console.error(e);
      }
    },
    {
      pending: (state) => {
        if (state.workingProject) {
          state.isSettingsUpdating = true;
        }
      },
      fulfilled: (state, action) => {
        if (state.workingProject && action.payload) {
          state.workingProject.favicon = action.payload?.favicon;
          state.isSettingsUpdating = false;
        }
      },
      rejected: (state) => {
        state.isSettingsUpdating = false;
      },
    }
  ),
});
